import { useForm } from '@conform-to/react';
import { parse } from '@conform-to/zod';
import { Divider } from '@intersport-digital/react';
import udsStyleSheetUrl from '@intersport-digital/uds-css/dist/css/uds.css';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  type DataFunctionArgs,
  type HeadersFunction,
  type MetaFunction,
  type LinksFunction } from
'@remix-run/node';
import {
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useFetchers,
  useLoaderData } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import { lazy, Suspense } from 'react';
import { z } from 'zod';
import { Confetti } from './components/confetti.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { ErrorList } from './components/forms.tsx';
import { EpicProgress } from './components/progress-bar.tsx';
import { EpicToaster } from './components/toaster.tsx';
import { Footer } from './components/ui/footer/footer.tsx';
import { Header } from './components/ui/header/header.tsx';
import { Icon, href as iconsHref } from './components/ui/icon.tsx';
import LinkProvider from './context/LinkContext.tsx';
import { getSession } from './services/sessions.tsx';
import fontStyleSheetUrl from './styles/font.css';
import tailwindStyleSheetUrl from './styles/tailwind.css';
import { authenticator, getUserId } from './utils/auth.server.ts';
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx';
import { getConfetti } from './utils/confetti.server.ts';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import {
  combineHeaders,
  getDomainUrl,
  invariantResponse } from
'./utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { useRequestInfo } from './utils/request-info.ts';
import { type Theme, setTheme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';

const PreviewProvider = lazy(
  () => import('./components//Preview/PreviewProvider.tsx')
);

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
  { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
  { rel: 'preload', href: udsStyleSheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' }, (
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const),

  //These should match the css preloads above to avoid css as render blocking resource
  { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
  { rel: 'stylesheet', href: fontStyleSheetUrl },
  { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  { rel: 'stylesheet', href: udsStyleSheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
  { title: data ? 'INTERSPORT Rent' : 'Något gick fel | INTERSPORT Rent' },
  { name: 'description', content: `Uthyrning av sportutrustning.` }];

};

export async function loader({ request }: DataFunctionArgs) {
  const session = await getSession(request.headers.get('Cookie'));
  const token = session.get('preview');
  const preview = token ? { token } : undefined;

  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const user = userId ?
  await time(
    () =>
    prisma.user.findUniqueOrThrow({
      select: {
        id: true,
        name: true,
        username: true,
        image: { select: { id: true } },
        roles: {
          select: {
            name: true,
            permissions: {
              select: { entity: true, action: true, access: true }
            }
          }
        }
      },
      where: { id: userId }
    }),
    { timings, type: 'find user', desc: 'find user in root' }
  ) :
  null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await authenticator.logout(request, { redirectTo: '/' });
  }
  const { toast, headers: toastHeaders } = await getToast(request);
  const { confettiId, headers: confettiHeaders } = getConfetti(request);

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request)
        }
      },
      ENV: getEnv(),
      toast,
      confettiId,
      preview
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
        confettiHeaders
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

const ThemeFormSchema = z.object({
  theme: z.enum(['system', 'light', 'dark'])
});

export async function action({ request }: DataFunctionArgs) {
  const formData = await request.formData();
  invariantResponse(
    formData.get('intent') === 'update-theme',
    'Invalid intent',
    { status: 400 }
  );
  const submission = parse(formData, {
    schema: ThemeFormSchema
  });
  if (submission.intent !== 'submit') {
    return json(({ status: 'success', submission } as const));
  }
  if (!submission.value) {
    return json(({ status: 'error', submission } as const), { status: 400 });
  }
  const { theme } = submission.value;

  const responseInit = {
    headers: { 'set-cookie': setTheme(theme) }
  };
  return json({ success: true, submission }, responseInit);
}

function Document({
  children,
  nonce,
  theme = 'light',
  env = {}





}: {children: React.ReactNode;nonce: string;theme?: Theme;env?: Record<string, string>;}) {
  return (
    <html lang="sv" className={`${theme}`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body>
				<LinkProvider>{children}</LinkProvider>
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />

				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

function App() {
  const data = useLoaderData<typeof loader>();

  const { preview } = data;

  const nonce = useNonce();
  // const theme = useTheme()
  // const matches = useMatches()
  // const isOnSearchPage = matches.find(m => m.id === 'routes/users+/index')

  return (
    <Document nonce={nonce} env={data.ENV}>
			<div className="flex h-screen flex-col justify-between">
				<Header />
				<div className="flex-1">
					{preview?.token ?
          <PreviewProvider token={preview.token}>
							<Suspense fallback={<Outlet />}>
								<Outlet />
							</Suspense>
						</PreviewProvider> :

          <Outlet />}

				</div>
				<Footer />
			</div>
			<Confetti id={data.confettiId} />
			<EpicToaster toast={data.toast} />
			<EpicProgress />
			<div className="drawer hidden" data-testid="Drawer__root">
				<div
          data-testid="Drawer__mask"
          // className="drawer-mask drawer-mask--drawer fade-in"
          className="drawer-mask drawer-mask--drawer fade-out">
        </div>
				<div
        // className="drawer__content-wrapper slide-in drawer__content-wrapper--position-right !w-11/12"
        className="drawer__content-wrapper slide-out drawer__content-wrapper--position-right !w-11/12"
        data-testid="Drawer__content-wrapper">

					<div className="drawer__content">
						<div className="drawer__wrapper-body">
							<div className="drawer__header">
								<div className="drawer__header-top">
									<div className="d-flex align-items-center flex-grow-1">
										<div className="flex-grow-1">
											<Link
                        className="text-color-blue"
                        data-testid="link-component"
                        to="/">

												<svg
                          className="header-brand__desktop"
                          data-testid="svg-icon"
                          width="155"
                          height="16">

													<svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 155 16"
                            width="155"
                            height="16">

														<path
                              fill="#E30613"
                              d="M21.784 1.826c0-1.04.68-1.741 1.74-1.741 1.062 0 1.741.7 1.741 1.74v12.35c0 1.04-.679 1.74-1.74 1.74s-1.74-.7-1.74-1.74V1.825zM28.19 2.123c0-1.444.7-2.038 2.187-2.038.721 0 1.358.276 1.74.913l6.09 9.825h.042V1.762c0-1.125.637-1.677 1.674-1.677 1.04 0 1.677.552 1.677 1.677V13.75c0 1.38-.594 2.165-2.059 2.165-1.103 0-1.464-.233-1.91-.934l-6.047-9.55h-.043v8.807c0 1.125-.636 1.677-1.677 1.677-1.04 0-1.677-.552-1.677-1.677V2.123h.003zM47.132 3.29H44.65c-.998 0-1.804-.51-1.804-1.464 0-.956.806-1.465 1.804-1.465h8.44c.998 0 1.805.51 1.805 1.465 0 .955-.807 1.464-1.804 1.464H50.61v10.887c0 1.04-.68 1.74-1.741 1.74s-1.74-.7-1.74-1.74V3.29h.002zM56.154 2.399c0-1.55.807-2.038 2.035-2.038h7.632c1.019 0 1.761.297 1.761 1.4 0 1.105-.74 1.402-1.761 1.402h-6.188v3.309h5.36c.913 0 1.634.254 1.634 1.337s-.721 1.337-1.634 1.337h-5.36v3.694h6.357c1.02 0 1.762.297 1.762 1.4 0 1.105-.743 1.402-1.762 1.402h-7.8c-1.232 0-2.036-.488-2.036-2.038V2.399zM69.278 2.399c0-1.55.807-2.038 2.038-2.038h5.254c4.139 0 5.24 2.271 5.24 4.243 0 1.655-.976 3.29-2.63 3.672v.042c1.784.255 2.102 1.55 2.102 3.1 0 1.761.467 2.417.467 3.141 0 .807-.828 1.359-1.634 1.359-1.91 0-2.06-1.847-2.06-2.463 0-2.674-.488-3.754-2.398-3.754h-2.9v4.476c0 1.04-.68 1.74-1.741 1.74s-1.74-.7-1.74-1.74V2.4h.002zm3.479 4.752h2.94c1.316 0 2.632-.34 2.632-2.06 0-1.634-.998-2.058-2.526-2.058h-3.046V7.15z">
                            </path>
														<path
                              fill="#164196"
                              d="M125.931 2.399c0-1.55.806-2.038 2.037-2.038h5.254c4.137 0 5.241 2.271 5.241 4.243 0 1.655-.977 3.29-2.63 3.672v.042c1.783.255 2.102 1.55 2.102 3.1 0 1.761.467 2.417.467 3.141 0 .807-.828 1.359-1.635 1.359-1.91 0-2.059-1.847-2.059-2.463 0-2.674-.488-3.754-2.399-3.754h-2.897v4.476c0 1.04-.679 1.74-1.741 1.74-1.061 0-1.74-.7-1.74-1.74V2.4zm3.481 4.752h2.94c1.316 0 2.632-.34 2.632-2.06 0-1.634-.998-2.058-2.526-2.058h-3.046V7.15zM92.129 6.79c3.181.722 3.99 2.611 3.99 4.394 0 2.76-2.08 4.816-6.28 4.816-4.413 0-6.387-2.208-6.387-3.863 0-.85.616-1.444 1.465-1.444 1.889 0 1.4 2.76 4.922 2.76 1.804 0 2.802-.998 2.802-2.017 0-.615-.297-1.295-1.486-1.592l-3.924-.998c-3.16-.806-3.734-2.547-3.734-4.181C83.492 1.274 86.634 0 89.582 0c2.717 0 5.92 1.528 5.92 3.564 0 .87-.744 1.38-1.593 1.38-1.613 0-1.316-2.272-4.564-2.272-1.613 0-2.504.743-2.504 1.804 0 1.062 1.273 1.401 2.374 1.656l2.914.658zM97.645 2.399c0-1.55.807-2.038 2.038-2.038h4.787c2.929 0 5.283 1.443 5.283 4.837 0 2.78-1.932 4.837-5.304 4.837h-3.322v4.137c0 1.04-.68 1.74-1.74 1.74-1.062 0-1.742-.7-1.742-1.74V2.399zm3.479 4.964h2.707c1.401 0 2.438-.849 2.438-2.165 0-1.528-1.083-2.165-2.802-2.165h-2.346v4.33h.003zM117.4 0c5.009 0 6.646 3.333 6.646 7.809 0 4.306-1.424 8.191-6.646 8.191-5.474 0-6.647-3.563-6.647-8.191 0-4.476 1.64-7.809 6.647-7.809zm0 13.198c3.16 0 3.165-2.46 3.165-5.389 0-2.93-.321-5.007-3.165-5.007-2.845 0-3.166 2.08-3.166 5.007 0 2.93-.103 5.389 3.166 5.389zM143.096 3.29h-2.481c-.998 0-1.804-.51-1.804-1.464 0-.956.806-1.465 1.804-1.465h8.44c.998 0 1.805.51 1.805 1.465 0 .955-.807 1.464-1.805 1.464h-2.481v10.887c0 1.04-.679 1.74-1.74 1.74-1.062 0-1.741-.7-1.741-1.74V3.29h.003zM154.027 2.545l-.353-.714c.209-.05.334-.212.334-.396a.413.413 0 00-.191-.337c-.122-.08-.263-.095-.409-.095h-.674v1.542h.226v-.67h.456l.335.667h.276v.003zm-1.067-.826V1.17h.448c.093 0 .17.003.25.05a.23.23 0 01.124.213.26.26 0 01-.092.201c-.088.08-.189.085-.308.085h-.422z">
                            </path>
														<path
                              fill="#164196"
                              d="M153.303 3.17a1.357 1.357 0 010-2.711 1.357 1.357 0 010 2.712zm0-2.472c-.615 0-1.12.501-1.12 1.12a1.118 1.118 0 101.12-1.12z">
                            </path>
														<path
                              fill="#E30613"
                              d="M0 3.614a3.42 3.42 0 116.84 0 3.42 3.42 0 01-6.84 0z">
                            </path>
														<path
                              fill="#164196"
                              d="M3.426 15.767c-.467 0-.847-.414-.847-.924V9.236c0-.509.366-.923.847-.923 5.38 0 5.808-7.283 13.54-7.283.467 0 .846.413.846.923V7.5c0 .51-.38.924-.846.924-5.273 0-5.602 7.341-13.54 7.341z">
                            </path>
														<defs>
															<clipPath id="clip0">
																<path fill="#fff" d="M0 0h154.659v16H0z"></path>
															</clipPath>
														</defs>
													</svg>
												</svg>
												<svg
                          className="header-brand__mobile"
                          data-testid="svg-icon"
                          width="116"
                          height="13">

													<svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 155 16"
                            width="116"
                            height="13">

														<path
                              fill="#E30613"
                              d="M21.784 1.826c0-1.04.68-1.741 1.74-1.741 1.062 0 1.741.7 1.741 1.74v12.35c0 1.04-.679 1.74-1.74 1.74s-1.74-.7-1.74-1.74V1.825zM28.19 2.123c0-1.444.7-2.038 2.187-2.038.721 0 1.358.276 1.74.913l6.09 9.825h.042V1.762c0-1.125.637-1.677 1.674-1.677 1.04 0 1.677.552 1.677 1.677V13.75c0 1.38-.594 2.165-2.059 2.165-1.103 0-1.464-.233-1.91-.934l-6.047-9.55h-.043v8.807c0 1.125-.636 1.677-1.677 1.677-1.04 0-1.677-.552-1.677-1.677V2.123h.003zM47.132 3.29H44.65c-.998 0-1.804-.51-1.804-1.464 0-.956.806-1.465 1.804-1.465h8.44c.998 0 1.805.51 1.805 1.465 0 .955-.807 1.464-1.804 1.464H50.61v10.887c0 1.04-.68 1.74-1.741 1.74s-1.74-.7-1.74-1.74V3.29h.002zM56.154 2.399c0-1.55.807-2.038 2.035-2.038h7.632c1.019 0 1.761.297 1.761 1.4 0 1.105-.74 1.402-1.761 1.402h-6.188v3.309h5.36c.913 0 1.634.254 1.634 1.337s-.721 1.337-1.634 1.337h-5.36v3.694h6.357c1.02 0 1.762.297 1.762 1.4 0 1.105-.743 1.402-1.762 1.402h-7.8c-1.232 0-2.036-.488-2.036-2.038V2.399zM69.278 2.399c0-1.55.807-2.038 2.038-2.038h5.254c4.139 0 5.24 2.271 5.24 4.243 0 1.655-.976 3.29-2.63 3.672v.042c1.784.255 2.102 1.55 2.102 3.1 0 1.761.467 2.417.467 3.141 0 .807-.828 1.359-1.634 1.359-1.91 0-2.06-1.847-2.06-2.463 0-2.674-.488-3.754-2.398-3.754h-2.9v4.476c0 1.04-.68 1.74-1.741 1.74s-1.74-.7-1.74-1.74V2.4h.002zm3.479 4.752h2.94c1.316 0 2.632-.34 2.632-2.06 0-1.634-.998-2.058-2.526-2.058h-3.046V7.15z">
                            </path>
														<path
                              fill="#164196"
                              d="M125.931 2.399c0-1.55.806-2.038 2.037-2.038h5.254c4.137 0 5.241 2.271 5.241 4.243 0 1.655-.977 3.29-2.63 3.672v.042c1.783.255 2.102 1.55 2.102 3.1 0 1.761.467 2.417.467 3.141 0 .807-.828 1.359-1.635 1.359-1.91 0-2.059-1.847-2.059-2.463 0-2.674-.488-3.754-2.399-3.754h-2.897v4.476c0 1.04-.679 1.74-1.741 1.74-1.061 0-1.74-.7-1.74-1.74V2.4zm3.481 4.752h2.94c1.316 0 2.632-.34 2.632-2.06 0-1.634-.998-2.058-2.526-2.058h-3.046V7.15zM92.129 6.79c3.181.722 3.99 2.611 3.99 4.394 0 2.76-2.08 4.816-6.28 4.816-4.413 0-6.387-2.208-6.387-3.863 0-.85.616-1.444 1.465-1.444 1.889 0 1.4 2.76 4.922 2.76 1.804 0 2.802-.998 2.802-2.017 0-.615-.297-1.295-1.486-1.592l-3.924-.998c-3.16-.806-3.734-2.547-3.734-4.181C83.492 1.274 86.634 0 89.582 0c2.717 0 5.92 1.528 5.92 3.564 0 .87-.744 1.38-1.593 1.38-1.613 0-1.316-2.272-4.564-2.272-1.613 0-2.504.743-2.504 1.804 0 1.062 1.273 1.401 2.374 1.656l2.914.658zM97.645 2.399c0-1.55.807-2.038 2.038-2.038h4.787c2.929 0 5.283 1.443 5.283 4.837 0 2.78-1.932 4.837-5.304 4.837h-3.322v4.137c0 1.04-.68 1.74-1.74 1.74-1.062 0-1.742-.7-1.742-1.74V2.399zm3.479 4.964h2.707c1.401 0 2.438-.849 2.438-2.165 0-1.528-1.083-2.165-2.802-2.165h-2.346v4.33h.003zM117.4 0c5.009 0 6.646 3.333 6.646 7.809 0 4.306-1.424 8.191-6.646 8.191-5.474 0-6.647-3.563-6.647-8.191 0-4.476 1.64-7.809 6.647-7.809zm0 13.198c3.16 0 3.165-2.46 3.165-5.389 0-2.93-.321-5.007-3.165-5.007-2.845 0-3.166 2.08-3.166 5.007 0 2.93-.103 5.389 3.166 5.389zM143.096 3.29h-2.481c-.998 0-1.804-.51-1.804-1.464 0-.956.806-1.465 1.804-1.465h8.44c.998 0 1.805.51 1.805 1.465 0 .955-.807 1.464-1.805 1.464h-2.481v10.887c0 1.04-.679 1.74-1.74 1.74-1.062 0-1.741-.7-1.741-1.74V3.29h.003zM154.027 2.545l-.353-.714c.209-.05.334-.212.334-.396a.413.413 0 00-.191-.337c-.122-.08-.263-.095-.409-.095h-.674v1.542h.226v-.67h.456l.335.667h.276v.003zm-1.067-.826V1.17h.448c.093 0 .17.003.25.05a.23.23 0 01.124.213.26.26 0 01-.092.201c-.088.08-.189.085-.308.085h-.422z">
                            </path>
														<path
                              fill="#164196"
                              d="M153.303 3.17a1.357 1.357 0 010-2.711 1.357 1.357 0 010 2.712zm0-2.472c-.615 0-1.12.501-1.12 1.12a1.118 1.118 0 101.12-1.12z">
                            </path>
														<path
                              fill="#E30613"
                              d="M0 3.614a3.42 3.42 0 116.84 0 3.42 3.42 0 01-6.84 0z">
                            </path>
														<path
                              fill="#164196"
                              d="M3.426 15.767c-.467 0-.847-.414-.847-.924V9.236c0-.509.366-.923.847-.923 5.38 0 5.808-7.283 13.54-7.283.467 0 .846.413.846.923V7.5c0 .51-.38.924-.846.924-5.273 0-5.602 7.341-13.54 7.341z">
                            </path>
														<defs>
															<clipPath id="clip0">
																<path fill="#fff" d="M0 0h154.659v16H0z"></path>
															</clipPath>
														</defs>
													</svg>
												</svg>
											</Link>
										</div>
										<a
                      className="text-color-blue ms-6 hidden"
                      data-testid="link-component"
                      href="/">

											<svg className="icon-container" data-testid="svg-icon">
												<svg
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24">

													<path
                            stroke="currentColor"
                            stroke-width="2"
                            d="M19.876 7.858v.004c.087.338.129.687.123 1.036v.021a3.69 3.69 0 01-.495 1.872l-.006.01-.006.012c-.16.291-.362.561-.599.796l-.006.006L12 18.578l-6.887-6.963-.003-.002a3.933 3.933 0 01-.62-.81 4.23 4.23 0 01-.368-.922 3.896 3.896 0 01-.121-.968v-.015c-.006-.35.036-.698.122-1.036l.003-.01c.161-.657.5-1.253.977-1.722l.009-.009a3.85 3.85 0 011.232-.835 3.76 3.76 0 012.876 0c.422.18.81.431 1.151.741l.022.026.028.035.032.032.836.845.711.719.711-.72.836-.844.031-.032.03-.035.02-.026c.341-.31.73-.56 1.152-.741a3.76 3.76 0 012.876 0c.46.195.878.478 1.232.835l.006.006.006.006c.234.228.432.492.588.782l.008.015.008.015c.165.285.29.593.372.913zm-.278-2.441z">
                          </path>
												</svg>
											</svg>
										</a>
										<a
                      className="text-color-blue ms-6 hidden"
                      data-testid="link-component"
                      href="/shopping-cart">

											<svg className="icon-container" data-testid="svg-icon">
												<svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          className="icon-container icon-shopping-bag"
                          viewBox="0 0 24 24">

													<path
                            fill="currentColor"
                            fill-rule="evenodd"
                            d="M16 7h3v9a4 4 0 01-4 4H9a4 4 0 01-4-4V7h3c0-1.015.312-2.023 1.013-2.788C9.727 3.432 10.758 3 12 3s2.273.432 2.987 1.212C15.688 4.977 16 5.985 16 7zm-2.487-1.437C13.812 5.889 14 6.38 14 7h-4c0-.619.188-1.11.487-1.437C10.773 5.25 11.242 5 12 5c.758 0 1.227.25 1.513.563zM7 9v7a2 2 0 002 2h6a2 2 0 002-2V9H7z"
                            clip-rule="evenodd">
                          </path>
												</svg>
											</svg>
										</a>
									</div>
									<div className="drawer__close" data-testid="Drawer__close">
										<svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      className="icon-container icon-exit"
                      viewBox="0 0 24 24">

											<path
                        fill="currentColor"
                        fill-rule="evenodd"
                        d="M5.778 17.091L17.09 5.778l1.415 1.414L7.192 18.506 5.778 17.09z">
                      </path>
											<path
                        fill="currentColor"
                        fill-rule="evenodd"
                        d="M6.909 5.777l11.314 11.314-1.415 1.414L5.495 7.192l1.414-1.415z">
                      </path>
										</svg>
									</div>
								</div>
							</div>
							<div className="drawer__body drawer__body--with-footer uds-scroll uds-scroll--sizeless">
								<div className="mobile-menu">
									<nav aria-label="Mobile navigation" className="mb-8">
										<ul
                      className="mobile-nav-list"
                      data-testid="LinkList-component">

											<li className="mobile-nav-link">
												<div data-testid="tabLink-component">
													<Link
                            className="mobile-nav-item"
                            data-testid="link-component"
                            to="/butiker">

														<div className="align-center flex flex-row gap-4">
															<Icon name="map-pin" /> Butiker
														</div>
														<svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="none"
                              className="icon-container icon-next"
                              viewBox="0 0 24 24">

															<path
                                stroke="currentColor"
                                stroke-width="2"
                                d="M9 4l5.55 7.4a1 1 0 010 1.2L9 20">
                              </path>
														</svg>
													</Link>
												</div>
											</li>
											<Divider
                        size="thin"
                        color="white"
                        classes={{ root: 'my-5' }} />

											<li className="mobile-nav-link">
												<div data-testid="tabLink-component">
													<a
                            className="mobile-nav-item"
                            data-testid="link-component"
                            href="https://www.intersport.se/kundservice">

														<div className="align-center flex flex-row gap-4 font-normal">
															<Icon
                                name="question-mark-circled"
                                strokeWidth={2}
                                className="!font-bold" />
                              {' '}
															Kundservice
														</div>
														<svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="none"
                              className="icon-container icon-next"
                              viewBox="0 0 24 24">

															<path
                                stroke="currentColor"
                                stroke-width="2"
                                d="M9 4l5.55 7.4a1 1 0 010 1.2L9 20">
                              </path>
														</svg>
													</a>
												</div>
											</li>
										</ul>
									</nav>
								</div>
								<nav className="mobile-footer-nav" aria-label="Mobile footer">
									<ul
                    className="mobile-footer-list justify-content-evenly flex flex-row"
                    data-testid="LinkList-component">

										<li
                      className="mobile-footer-link mx-2"
                      data-testid="linklist-item">

											<Link
                        className="mobile-footer-item pe-0 ps-0"
                        data-testid="link-component"
                        to="/butiker">

												<Icon name="map-pin" />
												<span className="lh-default drawer-link-text">
													Butiker
												</span>
											</Link>
										</li>
										<li
                      className="mobile-footer-link mx-2"
                      data-testid="linklist-item">

											<Link
                        className="mobile-footer-item pe-0 ps-0"
                        data-testid="link-component"
                        to="/login">

												<svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24">

													<path
                            fill="#fff"
                            fill-rule="evenodd"
                            d="M12 3a5 5 0 00-3.232 8.815c.174.148.223.278.23.347-.37.288-1.07.611-1.942.95-.305.118-.603.229-.893.336l-.413.154c-.397.148-.795.302-1.092.45A3 3 0 003 16.737V20h18v-3.264a3 3 0 00-1.658-2.683 12.164 12.164 0 00-1.092-.451l-.413-.154c-.29-.107-.588-.218-.893-.336-.872-.339-1.573-.662-1.943-.95.008-.069.057-.2.23-.347A5 5 0 0012 3zM9 8a3 3 0 114.939 2.29c-.485.41-.939 1.066-.939 1.892 0 .399.107.976.584 1.401.655.586 1.75 1.05 2.636 1.393.32.124.645.245.943.355l.386.144c.41.153.71.273.898.367a1 1 0 01.553.894V18H5v-1.264a1 1 0 01.553-.894c.188-.095.489-.214.898-.367l.386-.144c.298-.11.624-.23.943-.354.887-.345 1.98-.808 2.636-1.394.476-.425.584-1.002.584-1.401 0-.826-.454-1.481-.939-1.892A2.991 2.991 0 019 8z"
                            clip-rule="evenodd">
                          </path>
												</svg>
												<span className="lh-default drawer-link-text">
													Mina sidor
												</span>
											</Link>
										</li>
										<li
                      className="mobile-footer-link mx-2"
                      data-testid="linklist-item">

											<a
                        className="mobile-footer-item pe-0 ps-0"
                        data-testid="link-component"
                        href="/kundservice">

												<svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24">

													<path
                            fill="#fff"
                            fill-rule="evenodd"
                            d="M4 12a8 8 0 1116 0 8 8 0 01-16 0zm8-10C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-.924 10.557c-.08.232-.12.506-.12.822v.32h2.196v-.125c0-.12.022-.242.067-.362.053-.121.111-.219.174-.293l.924-1.212c.188-.25.348-.52.482-.807.134-.288.201-.613.201-.975 0-.483-.085-.905-.255-1.268a2.531 2.531 0 00-.656-.919 2.768 2.768 0 00-.964-.543A3.263 3.263 0 0012 7c-.402 0-.786.065-1.152.195a2.83 2.83 0 00-.964.571 2.612 2.612 0 00-.643.92c-.16.361-.241.78-.241 1.253h2.196c0-.251.063-.46.188-.627.125-.167.33-.25.616-.25.268 0 .469.083.603.25.134.158.2.357.2.599a.924.924 0 01-.107.39c-.071.14-.156.27-.254.39l-.938 1.142a2.206 2.206 0 00-.428.724zm2.103 2.2h-2.25V17h2.25v-2.242z"
                            clip-rule="evenodd">
                          </path>
												</svg>
												<span className="lh-default drawer-link-text">
													Kundtjänst
												</span>
											</a>
										</li>
									</ul>
								</nav>
							</div>
						</div>
					</div>
				</div>
			</div>{' '}
		</Document>);

}
export default withSentry(App);

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints();
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode;
  }
  return requestInfo.userPrefs.theme ?? hints.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();

  const themeFetcher = fetchers.find(
    (f) => f.formData?.get('intent') === 'update-theme'
  );

  if (themeFetcher && themeFetcher.formData) {
    const submission = parse(themeFetcher.formData, {
      schema: ThemeFormSchema
    });
    return submission.value?.theme;
  }
}

function ThemeSwitch({ userPreference }: {userPreference?: Theme | null;}) {
  const fetcher = useFetcher<typeof action>();

  const [form] = useForm({
    id: 'theme-switch',
    lastSubmission: fetcher.data?.submission,
    onValidate({ formData }) {
      return parse(formData, { schema: ThemeFormSchema });
    }
  });

  const optimisticMode = useOptimisticThemeMode();
  const mode = optimisticMode ?? userPreference ?? 'system';
  const nextMode =
  mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system';
  const modeLabel = {
    light:
    <Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>,

    dark:
    <Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>,

    system:
    <Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>

  };

  return (
    <fetcher.Form method="POST" {...form.props}>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
          name="intent"
          value="update-theme"
          type="submit"
          className="flex h-8 w-8 cursor-pointer items-center justify-center">

					{modeLabel[mode]}
				</button>
			</div>
			<ErrorList errors={form.errors} id={form.errorId} />
		</fetcher.Form>);

}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}